import Link from 'next/link';
import ReactMarkdown, { type Components } from 'react-markdown';
import remarkGfm from 'remark-gfm';

/**
 * 도움말 본문 한 장. `/help/<id>` 화면과, 앞으로 붙을 도움말 모달이 이것을 **함께** 쓴다 -
 * 두 벌로 만들면 반드시 갈라진다. 훅이 없어 서버·클라이언트 어느 쪽에서도 그릴 수 있다.
 *
 * 받는 글은 `[[id]]` 가 이미 풀린 마크다운이다(`lib/help/content.ts` 의 resolveLinks).
 */
function createComponents(onInternalLink?: (id: string) => void): Components {
  return {
  h2: ({ children }) => <h2 className="mt-10 mb-4 border-t border-border/70 pt-7 text-balance text-lg font-semibold text-foreground first:mt-0 first:border-0 first:pt-0">{children}</h2>,
  h3: ({ children }) => <h3 className="mt-7 mb-3 text-balance text-base font-semibold text-foreground">{children}</h3>,
  p: ({ children }) => <p className="my-4 text-pretty">{children}</p>,
  ul: ({ children }) => <ul className="my-4 list-disc space-y-2 pl-5 marker:text-muted-foreground">{children}</ul>,
  ol: ({ children }) => <ol className="my-4 list-decimal space-y-3 pl-5 marker:font-medium marker:text-muted-foreground">{children}</ol>,
  li: ({ children }) => <li className="pl-1 [&>p]:my-1">{children}</li>,
  strong: ({ children }) => <strong className="font-semibold text-foreground">{children}</strong>,
  a: ({ href, children }) =>
    href?.startsWith('/') ? (
      <Link href={href} onClick={onInternalLink ? (event) => {
        const id = href.match(/^\/help\/([a-z0-9-]+)$/)?.[1];
        if (id) { event.preventDefault(); onInternalLink(id); }
      } : undefined} className="font-medium text-primary underline-offset-4 hover:underline focus-visible:rounded-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring">
        {children}
      </Link>
    ) : (
      <a href={href} target="_blank" rel="noreferrer" className="font-medium text-primary underline-offset-4 hover:underline focus-visible:rounded-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring">
        {children}
      </a>
    ),
  table: ({ children }) => (
    <div className="my-5 overflow-x-auto rounded-md border">
      <table className="w-full text-sm">{children}</table>
    </div>
  ),
  th: ({ children }) => <th className="border-b bg-muted/50 px-3 py-2 text-left font-medium">{children}</th>,
  td: ({ children }) => <td className="border-b px-3 py-2 align-top last:border-b-0">{children}</td>,
  code: ({ children }) => <code className="rounded bg-muted px-1 py-0.5 text-[0.9em]">{children}</code>,
  };
}

export function HelpMarkdown({ markdown, onInternalLink }: { markdown: string; onInternalLink?: (id: string) => void }) {
  return (
    <div className="text-[15px] leading-7 text-foreground/90 sm:text-base sm:leading-8">
      <ReactMarkdown remarkPlugins={[remarkGfm]} components={createComponents(onInternalLink)}>
        {markdown}
      </ReactMarkdown>
    </div>
  );
}
